import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useDraft } from "@/components/import/DraftContext";
import { ItemRow } from "@/components/import/ItemRow";
import { DiffDialog, ItemEditDialog } from "@/components/import/ItemDialogs";
import { Pager } from "@/components/common/PageParts";

const FILTERS = [["", "All cards"], ["missing_images", "Needs images"], ["error", "Needs fixes"], ["ready", "Ready"], ["existing", "Already exist"],
  ["skip", "Will skip"], ["finalized", "Imported"]];
const PAGE = 25;

function BulkActions() {
  const { id, refresh, draft } = useDraft();
  const run = async (action) => {
    try {
      const { data } = await api.post(`/imports/${id}/actions`, { action, scope: "existing" });
      toast.success(`${data.changed} card(s) set to ${action}`);
      refresh();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  if (!draft.summary.existing || draft.status !== "draft") return null;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-slate-400" data-testid="import-bulk-actions">
      <span>{draft.summary.existing} card(s) already exist:</span>
      <button className="btn-ghost px-2 py-1 text-xs" onClick={() => run("update")} data-testid="import-bulk-update">Update all</button>
      <button className="btn-ghost px-2 py-1 text-xs" onClick={() => run("skip")} data-testid="import-bulk-skip">Skip all</button>
      <button className="btn-ghost px-2 py-1 text-xs" onClick={() => run("create")} data-testid="import-bulk-create">Create all as new</button>
    </div>
  );
}

export function ItemList({ initialFilter = "", testid = "import-item-list" }) {
  const { id } = useDraft();
  const [state, setState] = useState(initialFilter);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [edit, setEdit] = useState(null);
  const [diff, setDiff] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["imports", id, "items", state, q, page],
    queryFn: () => api.get(`/imports/${id}/items`, { params: { state: state || undefined, q: q || undefined, page, page_size: PAGE } }).then((r) => r.data),
  });
  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-3">
        <input className="field min-w-0 flex-1 sm:max-w-xs" placeholder="Search name, import ID or number" value={q}
          onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid={`${testid}-search`} />
        <select className="field w-auto" value={state} onChange={(e) => { setState(e.target.value); setPage(1); }} data-testid={`${testid}-filter`}>
          {FILTERS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
        <BulkActions />
      </div>
      {isLoading ? <p className="text-sm text-slate-500">Loading…</p> : !data?.items.length
        ? <p className="py-8 text-center text-sm text-slate-500" data-testid={`${testid}-empty`}>No cards match.</p>
        : <ul className="divide-y divide-[#2A3447]">{data.items.map((it) => <ItemRow key={it.item_id} item={it} onEdit={setEdit} onDiff={setDiff} />)}</ul>}
      {data && <Pager page={page} pageSize={PAGE} total={data.total} onChange={setPage} testid={`${testid}-pager`} />}
      <ItemEditDialog item={edit} onClose={() => setEdit(null)} />
      <DiffDialog item={diff} onClose={() => setDiff(null)} />
    </div>
  );
}
